// RBAC Permission Utilities
// License-based access control layered on top of role-based permissions

export const SYSTEM_ROLES = {
  SUPERADMIN: 'superadmin',
  ORG_ADMIN: 'org_admin',
  ORG_MEMBER: 'org_member',
};

// License tiers — controls which top-level menus are accessible
export const LICENSE_TYPES = {
  BASIC: 'basic',
  PROFESSIONAL: 'professional',
  ENTERPRISE: 'enterprise',
  READ_ONLY: 'read_only',
};

// Menu groups mapped to license access
// basic        → my-dashboard, sales, ops, finance, reports (same as professional; restrictions handled by roles)
// professional → my-dashboard, sales, ops, finance, reports
// enterprise   → dashboard, my-dashboard, sales, ops, finance, reports
// read_only    → my-dashboard, ops (view-only + clock-in/out only)
export const LICENSE_MENU_ACCESS = {
  [LICENSE_TYPES?.BASIC]: {
    menus: ['my-dashboard', 'sales', 'ops', 'finance', 'reports'],
    canAccessDashboard: false,
    canAccessSales: true,
    canAccessOps: true,
    canAccessFinance: true,
    canAccessReports: true,
    canAccessSettings: false,
    canAccessData: false,
    directTicketAccess: false,
  },
  [LICENSE_TYPES?.PROFESSIONAL]: {
    menus: ['my-dashboard', 'sales', 'ops', 'finance', 'reports'],
    canAccessDashboard: false,
    canAccessSales: true,
    canAccessOps: true,
    canAccessFinance: true,
    canAccessReports: true,
    canAccessSettings: false,
    canAccessData: false,
    directTicketAccess: false,
  },
  [LICENSE_TYPES?.ENTERPRISE]: {
    menus: ['dashboard', 'my-dashboard', 'sales', 'ops', 'finance', 'reports'],
    canAccessDashboard: true,
    canAccessSales: true,
    canAccessOps: true,
    canAccessFinance: true,
    canAccessReports: true,
    canAccessSettings: true,
    canAccessData: true,
    directTicketAccess: false,
  },
  [LICENSE_TYPES?.READ_ONLY]: {
    menus: ['my-dashboard', 'ops'],
    canAccessDashboard: false,
    canAccessSales: false,
    canAccessOps: true, // view-only
    canAccessFinance: false,
    canAccessReports: false,
    canAccessSettings: false,
    canAccessData: false,
    directTicketAccess: false,
    isReadOnly: true, // cannot modify any data
    canClockInOut: true, // only allowed write action
  },
};

// Org admins always get enterprise-level menu access regardless of license
// (their role may still restrict specific screens)
const ORG_ADMIN_ROLES = ['superadmin', 'org_admin'];

export const SCREENS = {
  DASHBOARD: 'dashboard',
  MY_DASHBOARD: 'my-dashboard',
  SUPPORT_REQUESTS: 'support-request-management',
  TICKET_MANAGEMENT: 'ticket-management',
  TECHNICIAN_ASSIGNMENT: 'technician-assignment',
  CUSTOMER_DATABASE: 'customer-database',
  PERSONNEL_MANAGEMENT: 'personnel-management',
  INVENTORY_TRACKING: 'inventory-tracking-dashboard',
  INVENTORY_ANALYTICS: 'inventory-analytics-dashboard',
  REPORTS: 'reports',
  INVOICE_MANAGEMENT: 'invoice-management-center',
  QUOTE_MANAGEMENT: 'quote-management',
  AUDIT_LOG: 'audit-log',
  PRODUCTS: 'products',
  DATA_MANAGEMENT: 'data-management',
  ORGANIZATION_MANAGEMENT: 'organization-management',
  USER_ROLE_MANAGEMENT: 'user-role-management',
  CAPITAL_EQUIPMENT: 'capital-equipment-management',
  OPERATIONS_EQUIPMENT: 'operations-equipment-management',
  TIME_ENTRY_APPROVAL: 'time-entry-approval',
  TECHNICIAN_FIELD_LOG: 'technician-field-log',
};

/**
 * Get the effective license type for a user.
 * Org admins and superadmins always get enterprise-level access.
 */
export const getEffectiveLicense = (userProfile) => {
  if (!userProfile) return LICENSE_TYPES?.BASIC;
  if (ORG_ADMIN_ROLES?.includes(userProfile?.system_role)) {
    return LICENSE_TYPES?.ENTERPRISE;
  }
  return userProfile?.license_type || LICENSE_TYPES?.PROFESSIONAL;
};

/**
 * Get the license access config for a user.
 */
export const getLicenseAccess = (userProfile) => {
  const license = getEffectiveLicense(userProfile);
  return LICENSE_MENU_ACCESS?.[license] || LICENSE_MENU_ACCESS?.[LICENSE_TYPES?.PROFESSIONAL];
};

/**
 * Check if user is in read-only mode.
 * Read-only users cannot modify any data. Org admins CANNOT override this.
 */
export const isReadOnlyUser = (userProfile) => {
  if (!userProfile) return false;
  // Superadmin is never read-only
  if (userProfile?.system_role === SYSTEM_ROLES?.SUPERADMIN) return false;
  return userProfile?.license_type === LICENSE_TYPES?.READ_ONLY || userProfile?.is_read_only === true;
};

/**
 * Check if user is superadmin
 */
export const isSuperAdmin = (userProfile) => {
  return userProfile?.system_role === SYSTEM_ROLES?.SUPERADMIN;
};

/**
 * Check if user is org admin or higher
 */
export const isOrgAdmin = (userProfile) => {
  if (!userProfile) return false;
  return ORG_ADMIN_ROLES?.includes(userProfile?.system_role);
};

/**
 * Check if user has license access to a top-level menu group.
 * This is the first gate — role permissions apply within the allowed menus.
 */
export const hasLicenseMenuAccess = (userProfile, menuKey) => {
  if (!userProfile) return false;
  // Superadmin bypasses all license checks
  if (userProfile?.system_role === SYSTEM_ROLES?.SUPERADMIN) return true;
  const access = getLicenseAccess(userProfile);
  return access?.menus?.includes(menuKey) || false;
};

/**
 * Check if user has permission for a specific screen and action.
 * License tier is checked first, then role-based permissions.
 * For read-only users, write actions are always denied.
 * For users with a custom org role (org_role_id), checks org_role_permissions.
 */
export const hasPermission = (userProfile, screen, action = 'read') => {
  if (!userProfile) return false;
  // Superadmin bypasses all checks
  if (userProfile?.system_role === SYSTEM_ROLES?.SUPERADMIN) return true;

  // Read-only users: only allow read actions and clock-in/out
  if (isReadOnlyUser(userProfile)) {
    if (action !== 'read') {
      // Only clock-in/out write actions are allowed for read-only users
      if (action === 'clock_in' || action === 'clock_out') return true;
      return false;
    }
    // Read-only users can only read my-dashboard and ops screens
    const opsScreens = [
      SCREENS?.MY_DASHBOARD,
      SCREENS?.SUPPORT_REQUESTS,
      SCREENS?.TICKET_MANAGEMENT,
      SCREENS?.TECHNICIAN_ASSIGNMENT,
      SCREENS?.OPERATIONS_EQUIPMENT,
      SCREENS?.TECHNICIAN_FIELD_LOG,
    ];
    return opsScreens?.includes(screen);
  }

  // Org admins bypass granular permission checks
  if (isOrgAdmin(userProfile)) return true;

  // If user has a custom org role with permissions loaded, check them
  if (userProfile?.org_role_id && Array.isArray(userProfile?.permissions) && userProfile?.permissions?.length > 0) {
    const screenPerm = userProfile?.permissions?.find(p => p?.screen === screen);
    if (screenPerm) {
      const actionMap = {
        read: 'can_read',
        create: 'can_create',
        update: 'can_update',
        delete: 'can_delete',
        // Legacy aliases
        write: 'can_update',
        edit: 'can_update',
      };
      const permKey = actionMap?.[action];
      if (permKey) {
        return screenPerm?.[permKey] === true;
      }
    }
    // Screen not in permissions list — deny by default for custom-role users
    return false;
  }

  // For all other users (no custom role assigned), return true
  return true;
};

/**
 * Check if user can access a route based on license tier.
 * Maps route paths to menu groups.
 */
export const canAccessRoute = (userProfile, routePath) => {
  if (!userProfile) return false;
  if (userProfile?.system_role === SYSTEM_ROLES?.SUPERADMIN) return true;

  const license = getEffectiveLicense(userProfile);
  const access = LICENSE_MENU_ACCESS?.[license];
  if (!access) return false;

  // Always allow these routes for all authenticated users
  const alwaysAllowed = [
    '/dashboard', '/my-dashboard', '/login', '/forgot-password',
    '/reset-password', '/user-profile', '/account-security-center',
    '/technician-field-log', '/mobile-technician-app',
  ];
  if (alwaysAllowed?.some(p => routePath?.startsWith(p))) {
    // For basic/read-only, restrict dashboard
    if (routePath === '/dashboard' && !access?.canAccessDashboard) return false;
    return true;
  }

  // Sales routes
  const salesRoutes = [
    '/quote-management', '/quote-approval-queue', '/customer-price-list-management',
    '/customer-portal', '/customer-portal-preview', '/customer-database',
  ];
  if (salesRoutes?.some(p => routePath?.startsWith(p))) return access?.canAccessSales;

  // Ops routes
  const opsRoutes = [
    '/support-request-management', '/technician-assignment', '/ticket-management',
    '/pending-tickets-workflow', '/operations-equipment-management',
    '/mobile-technician-app', '/job-completion-report', '/rework-follow-up-tracking',
    '/supervisor-dashboard', '/technician-live-map', '/technician-job-map',
    '/staff-portal-activity', '/dispatch-view', '/field-checkin',
  ];
  if (opsRoutes?.some(p => routePath?.startsWith(p))) return access?.canAccessOps;

  // Finance routes
  const financeRoutes = [
    '/invoice-management-center', '/ar-aging-finance-dashboard',
    '/invoice-reconciliation-dashboard', '/payment-reminders-dunning',
    '/rental-revenue-dashboard', '/accounting-sync', '/subscription-billing',
    '/recurring-billing', '/failed-payment-recovery', '/billing-analytics',
  ];
  if (financeRoutes?.some(p => routePath?.startsWith(p))) return access?.canAccessFinance;

  // Reports routes
  const reportsRoutes = [
    '/reports', '/time-clock-report', '/technician-location-report',
    '/technician-route-reports', '/payroll-checkin-report',
  ];
  if (reportsRoutes?.some(p => routePath?.startsWith(p))) return access?.canAccessReports;

  // Settings / Data Management routes (enterprise only)
  const settingsRoutes = [
    '/organization-settings', '/company-users', '/subscription-management',
    '/custom-dashboard-builder', '/email-templates', '/tax-rate-settings',
    '/admin-operator-dashboard', '/organization-dashboard', '/time-entries-admin-monitor',
    '/audit-log', '/audit-activity-log', '/permission-audit-dashboard',
    '/user-role-management', '/personnel-management', '/security-access-control',
    '/security-access-control-management', '/data-management', '/inventory-tracking-dashboard',
    '/inventory-analytics-dashboard', '/email-analytics', '/capital-equipment-management',
    '/time-entry-approval', '/bulk-technician-correction', '/sms-templates',
    '/sms-conversations-dashboard', '/bulk-sms-outreach', '/notification-delivery-dashboard',
    '/email-delivery-test-suite', '/products',
  ];
  if (settingsRoutes?.some(p => routePath?.startsWith(p))) return access?.canAccessSettings;

  // Default: allow if enterprise, deny otherwise
  return access?.canAccessDashboard; // enterprise = true, others = false
};

/**
 * Get a human-readable label for a license type.
 */
export const getLicenseLabel = (licenseType) => {
  const labels = {
    [LICENSE_TYPES?.BASIC]: 'Basic',
    [LICENSE_TYPES?.PROFESSIONAL]: 'Professional',
    [LICENSE_TYPES?.ENTERPRISE]: 'Enterprise',
    [LICENSE_TYPES?.READ_ONLY]: 'Read-Only',
  };
  return labels?.[licenseType] || 'Professional';
};

/**
 * Get badge color for a license type.
 */
export const getLicenseBadgeColor = (licenseType) => {
  const colors = {
    [LICENSE_TYPES?.BASIC]: 'bg-gray-100 text-gray-700',
    [LICENSE_TYPES?.PROFESSIONAL]: 'bg-blue-100 text-blue-700',
    [LICENSE_TYPES?.ENTERPRISE]: 'bg-amber-100 text-amber-700',
    [LICENSE_TYPES?.READ_ONLY]: 'bg-green-100 text-green-700',
  };
  return colors?.[licenseType] || 'bg-blue-100 text-blue-700';
};
